import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { OnlineService, ConfigurationService, AxiosService } from '.';
import { ApiEndpoints } from '@/config/index';
import { LoggerService } from '@/utils';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { ApiIdentifers } from '@/config/index';
import { LoggerTypeEnum } from '@/enums/LoggerTypeEnums';
import { ShareFilterData } from '@/models/filter/ShareFilterData';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { ConfigurationFilterSection } from '@/models/filter/ConfigurationFilterSection';
import { ConfigurationFilterField } from '@/models/filter/ConfigurationFilterField';
import { DexieFilter } from '@/models/dexie/DexieFilter';
import { AuthStore } from '@/store/store.auth';
import { ILiteEvent, LiteEvent } from '@/models/event/liteEvent';

class FilterNewProvider {
    public lastSync?: Date;
    public syncTimer?: any;

    private usedConfigurationFilterSections: ConfigurationFilterSection[] = [];
    private usedFilterForDataSets: DexieFilter | undefined = undefined;
    private usedFilterForDataSetsHasUnsaved: boolean = false;

    private usedFilterForDataSetFilters: string[] = [];

    private readonly onFiltersUpdated = new LiteEvent<DexieFilter[]>();

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;

    // tslint:disable-next-line: no-empty
    constructor() {}

    /**
     * Get all unsynced events
     */
    public async GetAllSyncedFilters(): Promise<DexieFilter[]> {
        return new Promise<DexieFilter[]>((resolve, reject) => {
            this.dexieDB
                .getTable<DexieFilter>(this.dexieDB!.getTables().syncedFiltersNew)
                .toArray()
                .then((data) => {
                    const filters: DexieFilter[] = [];
                    data.forEach((x) => {
                        filters.push(new DexieFilter(x));
                    });
                    resolve(filters);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public async GetAllUnsyncedFilters(): Promise<DexieFilter[]> {
        return new Promise<DexieFilter[]>((resolve, reject) => {
            this.dexieDB
                .getTable<DexieFilter>(this.dexieDB!.getTables().unsyncedFiltersNew)
                .toArray()
                .then((data) => {
                    const filters: DexieFilter[] = [];
                    data.forEach((x) => {
                        filters.push(new DexieFilter(x));
                    });
                    resolve(filters);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public async AddUnsyncedFilters(unsyncedFilters: DexieFilter[]): Promise<number> {
        return await this.dexieDB.bulkPut<DexieFilter>(this.dexieDB!.getTables().unsyncedFiltersNew, unsyncedFilters);
    }

    public async AddSyncedFilters(syncedFilters: DexieFilter[]): Promise<number> {
        const existing = await this.GetAllSyncedFilters();
        const sync: DexieFilter[] = [];
        syncedFilters.forEach((s) => {
            if (existing.find((e) => e.filterUuid === s.filterUuid) === undefined) {
                sync.push(s);
            }
        });
        return await this.dexieDB.bulkPut<DexieFilter>(this.dexieDB!.getTables().syncedFiltersNew, sync);
    }

    public async AddOrUpdateUnsyncedFilter(unsyncedFilter: DexieFilter): Promise<number> {
        return await this.dexieDB.put<DexieFilter>(this.dexieDB!.getTables().unsyncedFiltersNew, unsyncedFilter);
    }

    public async AddOrUpdateSyncedFilter(inFilter: DexieFilter): Promise<void> {
        await this.dexieDB
            .getTable<DexieFilter>(this.dexieDB!.getTables().syncedFiltersNew)
            .where('filterUuid')
            .equals(inFilter.filterUuid)
            .toArray()
            .then((queryFilter) => {
                const filterToUpdate = queryFilter.find((f) => f.filterUuid === inFilter.filterUuid);
                if (filterToUpdate) {
                    inFilter.id = filterToUpdate.id;
                }
                return this.dexieDB.put<DexieFilter>(this.dexieDB!.getTables().syncedFiltersNew, inFilter);
            });
    }

    public async DeleteAllUnsyncedFilters(): Promise<void> {
        return this.dexieDB.clear<DexieFilter>(this.dexieDB!.getTables().unsyncedFiltersNew);
    }

    public async DeleteAllSyncedFilters(): Promise<void> {
        return this.dexieDB.clear<DexieFilter>(this.dexieDB!.getTables().syncedFiltersNew);
    }

    public async DeleteSyncedFilter(inSyncedfilter: DexieFilter): Promise<void> {
        return this.dexieDB.del<DexieFilter>(this.dexieDB!.getTables().syncedFiltersNew, inSyncedfilter.id);
    }

    public async DeleteUnsyncedFilter(inUnsyncedfilter: DexieFilter): Promise<void> {
        return this.dexieDB.del<DexieFilter>(this.dexieDB!.getTables().unsyncedFiltersNew, inUnsyncedfilter.id);
    }

    public get OnFiltersUpdated(): ILiteEvent<DexieFilter[]> {
        return this.onFiltersUpdated.expose();
    }

    public get UsedFilterForDataSets(): DexieFilter | undefined {
        return this.usedFilterForDataSets;
    }

    public set UsedFilterForDataSets(inFilter: DexieFilter | undefined) {
        this.usedFilterForDataSets = inFilter;
    }

    public get UsedFilterForDataSetFilters(): string[] {
        return this.usedFilterForDataSetFilters;
    }

    public set UsedFilterForDataSetFilters(inFilter: string[]) {
        this.usedFilterForDataSetFilters = inFilter;
    }

    public get UsedFilterForDataSetsHasUnsaved(): boolean {
        return this.usedFilterForDataSetsHasUnsaved;
    }

    public set UsedFilterForDataSetsHasUnsaved(inUnsaved: boolean) {
        this.usedFilterForDataSetsHasUnsaved = inUnsaved;
    }

    public ResetUsedFilterData(): void {
        this.usedFilterForDataSets = undefined;
        this.usedFilterForDataSetFilters = [];
        this.usedFilterForDataSetsHasUnsaved = false;
    }

    public generateUsedConfigurationFilterSections(): ConfigurationFilterSection[] {
        this.usedConfigurationFilterSections = [];
        ConfigurationService.completeConfigurationSections.forEach((section) => {
            if (section.identifier !== ApiIdentifers.machineOrder) {
                this.usedConfigurationFilterSections.push(new ConfigurationFilterSection(section));
            }
        });

        this.usedConfigurationFilterSections.forEach((section) => {
            this.sortSectionsFields(section.fields);
        });

        return this.usedConfigurationFilterSections;
    }

    public async getAllFilters(): Promise<DexieFilter[]> {
        return new Promise<DexieFilter[]>((resolve, reject) => {
            const filters: DexieFilter[] = [];
            this.GetAllUnsyncedFilters()
                .then((unsynced) => {
                    unsynced.forEach((uFilter) => {
                        filters.push(new DexieFilter(uFilter));
                    });

                    this.GetAllSyncedFilters()
                        .then((synced) => {
                            synced.forEach((sFilter) => {
                                filters.push(new DexieFilter(sFilter));
                            });

                            filters.sort((lhs, rhs) => {
                                return lhs.filterName.localeCompare(rhs.filterName);
                            });

                            resolve(filters);
                        })
                        .catch((error) => {
                            LoggerService.error(`Error fetching synced filters: `, error);
                            reject(error);
                        });
                })
                .catch((error) => {
                    LoggerService.error(`Error fetching unsynced filters: `, error);
                    reject(error);
                });
        });
    }

    public async RemoveFilter(inFilter: DexieFilter): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
                AxiosService.delete(ApiEndpoints.removeFilter, null, { id: inFilter.filterUuid })
                    .then(async (response) => {
                        resolve();
                        this.SyncFiltersWithServer();
                    })
                    .catch((error) => {
                        LoggerService.error(`Error on removing filter: `, error);
                        reject(error);
                    });
            } else {
                // handle offline removing
                inFilter.removeOnSync = true;
                inFilter.isSynched = false;
                this.AddOrUpdateUnsyncedFilter(inFilter)
                    .then(() => {
                        this.DeleteSyncedFilter(inFilter)
                            .then(() => {
                                resolve();
                            })
                            .catch((error) => {
                                reject(error);
                            });
                    })
                    .catch((error) => {
                        reject(error);
                    });
            }
        });
    }

    public async ShareFilter(shareFilter: ShareFilterData) {
        return new Promise(async (resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE) {
                AxiosService.post(ApiEndpoints.shareFilter, shareFilter)
                    .then((response) => {
                        resolve(response);
                        LoggerService.Log(LoggerTypeEnum.INFO, 'success');
                    })
                    .catch((error) => {
                        reject(error);
                    });
            }
        });
    }

    public async SyncFiltersWithServer(triggerEvent: boolean = true): Promise<void> {
        const localUnsyncedFilters = await this.GetAllUnsyncedFilters();
        const localSyncedFilters = await this.GetAllSyncedFilters();

        return new Promise<void>(async (resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
                LoggerService.debug(`localUnsyncedFilters.length: ${localUnsyncedFilters.length}`);

                this.uploadUnsyncedFilters(localUnsyncedFilters).then(() => {
                    // get filters from server
                    LoggerService.debug(`get filters from server`);
                    AxiosService.get(ApiEndpoints.getFilters, null)
                        .then(async (response) => {
                            LoggerService.debug(`filters from server: `, response.data);

                            if (response.data) {
                                const syncedFilters: DexieFilter[] = [];
                                response.data.forEach((filterToAdd: DexieFilter) => {
                                    syncedFilters.push(new DexieFilter(filterToAdd));
                                });

                                // update or add synced filters
                                this.updateAddSyncedFilters(syncedFilters, localSyncedFilters)
                                    .then(() => {
                                        // remove filters that are not anymore on backend side
                                        const filtersToDelete = localSyncedFilters.filter((f) => !syncedFilters.some((sf) => sf.filterUuid === f.filterUuid));
                                        if (filtersToDelete.length > 0) {
                                            for (const filterToDelete of filtersToDelete) {
                                                this.DeleteSyncedFilter(filterToDelete)
                                                    .catch((error) => {
                                                        LoggerService.error(error);
                                                    })
                                                    .finally(() => {
                                                        this.removeFilterFromFilterList(filterToDelete, filtersToDelete);
                                                        if (filtersToDelete.length === 0) {
                                                            resolve();
                                                            if (triggerEvent === true) {
                                                                this.triggerFiltersUpdated();
                                                            }
                                                        } else {
                                                            reject('Some double filters could not be deleted');
                                                        }
                                                    });
                                            }
                                        } else {
                                            resolve();
                                            if (triggerEvent === true) {
                                                this.triggerFiltersUpdated();
                                            }
                                        }
                                    })
                                    .catch((error) => {
                                        reject(error);
                                    });
                            } else {
                                reject('response.data was not set');
                            }
                        })
                        .catch((error) => {
                            LoggerService.error(`Error on fetching filters from server: `, error);
                            reject(error);
                        });
                });
            } else {
                LoggerService.LogWarn(`App app seems to be offline`, OnlineService.GetOnlineStatus);
                reject(OnlineService.GetOnlineStatus);
            }
        });
    }

    public async syncFiltersFromServerToDexieDB(syncedFiltersFromServer: DexieFilter[], fromDexieDB: DexieFilter[]) {
        await this.DeleteAllSyncedFilters();
        await this.AddSyncedFilters(syncedFiltersFromServer);
    }

    private triggerFiltersUpdated() {
        this.GetAllSyncedFilters().then((filters) => {
            this.onFiltersUpdated.trigger(filters);
        });
    }

    private sortSectionsFields(sectionFields: ConfigurationFilterField[]) {
        sectionFields.forEach((sectionField: ConfigurationFilterField) => {
            if (sectionField.type === FieldTypeEnum.SUBFIELD && sectionField.fields) {
                this.sortSectionsFields(sectionField.fields);
            }
        });

        sectionFields.sort(this.sortFields);
    }

    private sortFields(lhs: ConfigurationFilterField, rhs: ConfigurationFilterField): number {
        if ((lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD) && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 0;
        } else if (lhs.type !== FieldTypeEnum.SUBFIELD && lhs.type !== FieldTypeEnum.ACTION_SUBFIELD && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return -1;
        } else if (rhs.type !== FieldTypeEnum.SUBFIELD && rhs.type !== FieldTypeEnum.ACTION_SUBFIELD && (lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 1;
        }
        return 1;
    }

    private uploadUnsyncedFilters(inFilteres: DexieFilter[]): Promise<void> {
        const filtersToUpload: DexieFilter[] = [];
        const filtersWithError: DexieFilter[] = [];
        inFilteres.forEach((f) => {
            filtersToUpload.push(new DexieFilter(f));
        });

        return new Promise<void>((resolve) => {
            const checkAllDone = () => {
                if (filtersToUpload.length === 0) {
                    if (filtersWithError.length > 0) {
                        LoggerService.warn(`Some filters could not be synced correct: `, filtersWithError);
                    }
                    resolve();
                }
            };

            if (filtersToUpload.length === 0) {
                resolve();
            } else {
                for (const unsyncedFilter of filtersToUpload) {
                    if (unsyncedFilter.removeOnSync === true) {
                        // if waiting for deletion, delete
                        if (unsyncedFilter.filterUuid !== '') {
                            // if filterUuid is set, also delete on BE side
                            AxiosService.delete(ApiEndpoints.removeFilter, null, { id: unsyncedFilter.filterUuid })
                                .then(async (response) => {
                                    this.DeleteUnsyncedFilter(unsyncedFilter)
                                        .then(() => {
                                            this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                        })
                                        .catch((error) => {
                                            LoggerService.error(`Error on removing filter from unsnced list: `, error);
                                            filtersWithError.push(new DexieFilter(unsyncedFilter));
                                            this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                        })
                                        .finally(() => {
                                            checkAllDone();
                                        });
                                })
                                .catch((error) => {
                                    LoggerService.error(`Error on removing filter on API side: `, error);
                                    filtersWithError.push(new DexieFilter(unsyncedFilter));
                                    this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                })
                                .finally(() => {
                                    checkAllDone();
                                });
                        }
                    } else if (unsyncedFilter.filterUuid === '') {
                        // new filter
                        AxiosService.post(ApiEndpoints.addFilter, unsyncedFilter)
                            .then(async () => {
                                this.DeleteUnsyncedFilter(unsyncedFilter)
                                    .then(() => {
                                        this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                    })
                                    .catch((error) => {
                                        LoggerService.error(`Error on removing filter from unsnced list: `, error);
                                        filtersWithError.push(new DexieFilter(unsyncedFilter));
                                        this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                    })
                                    .finally(() => {
                                        checkAllDone();
                                    });
                            })
                            .catch((error) => {
                                LoggerService.error(`Error on adding unsynced filter: `, error, unsyncedFilter);
                                filtersWithError.push(new DexieFilter(unsyncedFilter));
                                this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                            })
                            .finally(() => {
                                checkAllDone();
                            });
                    } else {
                        // update filter
                        AxiosService.put(ApiEndpoints.updateFilter, unsyncedFilter)
                            .then((response) => {
                                LoggerService.debug(`update filter`, response);
                                this.AddOrUpdateSyncedFilter(unsyncedFilter)
                                    .then(async () => {
                                        this.DeleteUnsyncedFilter(unsyncedFilter)
                                            .then(() => {
                                                this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                            })
                                            .catch((error) => {
                                                LoggerService.error(`Error on removing filter from unsnced list: `, error);
                                                filtersWithError.push(new DexieFilter(unsyncedFilter));
                                                this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                            })
                                            .finally(() => {
                                                checkAllDone();
                                            });
                                    })
                                    .catch((error) => {
                                        LoggerService.error(`Error on updating unsynced filter: `, error, unsyncedFilter);
                                        filtersWithError.push(new DexieFilter(unsyncedFilter));
                                        this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                                    })
                                    .finally(() => {
                                        checkAllDone();
                                    });
                            })
                            .catch((error) => {
                                LoggerService.error(`Error on adding unsynced filter: `, error, unsyncedFilter);
                                filtersWithError.push(new DexieFilter(unsyncedFilter));
                                this.removeFilterFromFilterList(unsyncedFilter, filtersToUpload);
                            })
                            .finally(() => {
                                checkAllDone();
                            });
                    }
                }
            }
        });
    }

    private updateAddSyncedFilters(inFilters: DexieFilter[], inLocalSyncedFilters: DexieFilter[]): Promise<void> {
        const filtersToSync: DexieFilter[] = [];
        inFilters.forEach((inf) => {
            filtersToSync.push(new DexieFilter(inf));
        });

        return new Promise<void>((resolve) => {
            if (filtersToSync.length === 0) {
                resolve();
                return;
            }

            const checkAllDone = () => {
                if (filtersToSync.length === 0) {
                    resolve();
                }
            };

            for (const syncedFilter of filtersToSync) {
                const localSyncedFilter = inLocalSyncedFilters.find((f) => f.filterUuid === syncedFilter.filterUuid);
                if (localSyncedFilter) {
                    // update local synced filter
                    localSyncedFilter.SetValues(syncedFilter);
                    localSyncedFilter.isSynched = true;
                    this.AddOrUpdateSyncedFilter(localSyncedFilter)
                        .catch((error) => {
                            LoggerService.error(`Error on update synced filter: `, error);
                        })
                        .finally(() => {
                            this.removeFilterFromFilterList(syncedFilter, filtersToSync);
                            checkAllDone();
                        });
                } else {
                    // add synced filter
                    syncedFilter.isSynched = true;
                    this.AddOrUpdateSyncedFilter(syncedFilter)
                        .catch((error) => {
                            LoggerService.error(`Error on add new synced filter: `, error);
                        })
                        .finally(() => {
                            this.removeFilterFromFilterList(syncedFilter, filtersToSync);
                            checkAllDone();
                        });
                }
            }
        });
    }

    private removeFilterFromFilterList(inFilter: DexieFilter, inFilterList: DexieFilter[]): void {
        const index = inFilterList.findIndex((f) => f.filterUuid === inFilter.filterUuid);
        if (index >= 0) {
            inFilterList.splice(index, 1);
        } else {
            LoggerService.warn(`Could not find inFilter to remove from FilterList.`, inFilter, inFilterList);
        }
    }
}

export default FilterNewProvider;
